<template>
  <cl-chart-card>
    <template #title>
      <chart-banner-index v-if="!isPhsActive && !isSatActive" />
      <chart-title
        v-else
        :is-phs-active="isPhsActive"
        :is-sat-active="isSatActive"
      />
    </template>
    <template #filters>
      <chart-filters
        v-if="isPhsActive && isSatActive"
        v-model:selectedCustomer="selectedCustomer"
        v-model:selectedMonth="selectedMonth"
      />
    </template>
    <template #body>
      <chart
        :is-fetching="isRequestPending"
        :is-error="isRequestError"
        :sat-completed="satCompleted"
        :lures-clicked="luresClicked"
        :labels="labels"
        :is-phs-active="isPhsActive"
        :is-sat-active="isSatActive"
      />
    </template>
  </cl-chart-card>
</template>
<script lang="ts">
interface IProps {
  isPhsActive: boolean;
  isSatActive: boolean;
}
</script>
<script setup lang="ts">
import { ref, onMounted, watch, computed } from "vue";
import { useTrendChart } from "./trend-chart.state";
import type { Option } from "@/modules/SecurityAwarnessTraining/types/trend-chart";
import Chart from "./chart/chart.index.vue";
import ChartTitle from "./chart-title/chart-title.index.vue";
import ChartFilters from "./chart-filters/chart-filters.index.vue";
import {
  useRequestStatus,
  ERROR,
  PENDING,
  READY,
} from "@/composables/useRequestStatus";
import ChartBannerIndex from "./chart-banner/chart-banner-index.vue";

const props = defineProps<IProps>();
const { getChartData, chartData } = useTrendChart();
const { setRequestStatus, isRequestPending, isRequestError } = useRequestStatus(
  { type: PENDING },
);
const selectedMonth = ref<string | null>("Year");
const selectedCustomer = ref<Option | null>(null);
const satCompletedDummyData = [
  10, 40, 160, 50, 150, 350, 300, 50, 220, 300, 240, 150,
];
const luresClickedDummyData = [35, 30, 40, 25, 20, 45, 15, 40, 20, 60, 15, 25];

const satCompleted = computed<number[]>(() => {
  if (!props.isPhsActive && !props.isSatActive) return satCompletedDummyData;
  if (!props.isSatActive) return chartData.value.map(() => 0);
  return chartData.value.map((item) => item.sat_completed);
});

const luresClicked = computed<number[]>(() => {
  if (!props.isPhsActive && !props.isSatActive) return luresClickedDummyData;
  if (!props.isPhsActive) return chartData.value.map(() => 0);
  return chartData.value.map((item) => item.clicked);
});

const labels = computed<string[]>(() =>
  chartData.value.map((item) => {
    const date = new Date(item.date);
    const month = new Intl.DateTimeFormat("en", { month: "short" }).format(
      date,
    );
    const day = date.getDate();
    const year = date.getFullYear();

    return selectedMonth.value === "Year"
      ? `${month} ${year}`
      : `${month} ${day}, ${year}`;
  }),
);

const fetchChartData = async () => {
  setRequestStatus({ type: PENDING });
  try {
    await getChartData({
      params: {
        organisationUUID: selectedCustomer.value?.value,
        period: selectedMonth.value,
      },
    });
    setRequestStatus({ type: READY });
  } catch {
    setRequestStatus({ type: ERROR });
  }
};

onMounted(fetchChartData);
watch([selectedCustomer, selectedMonth], fetchChartData);
</script>
